import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Tooltip, Typography } from '@mui/material';
import useResponsive from '../hooks/useResponsive';
import { getSocialLinks } from '../utils/socialLinks';
import { useSelector } from '../redux/store';
import useLocales from '../hooks/useLocales';
import { setShowLiveChat } from '../redux/slices/settings';

const FloatingIcons = () => {
  const dispatch = useDispatch();
  const { translate } = useLocales();
  const { siteSettings } = useSelector((x) => x.lookup);
  const [chatLinks, setChatLinks] = useState([]);
  const isDesktop = useResponsive('up', 'lg');
  const [open, setOpen] = useState(false);

  const openLiveChat = () => {
    if (window.LiveChatWidget) {
      setTimeout(() => {
        window.LiveChatWidget.call('maximize');
      }, 100);
    }
  };

  useEffect(() => {
    const links = getSocialLinks(siteSettings).filter((item) => item.url);
    setChatLinks(links);
  }, [siteSettings]);

  if (!chatLinks.length) return null;

  const itemHeight = 40;
  const panelHeight = chatLinks.length * itemHeight;

  return (
    <>
      {isDesktop && (
        <Box
          sx={{
            position: 'fixed',
            right: 0,
            bottom: 200,
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1300,
            gap: 1,
          }}
        >
          {chatLinks.map((item) => (
            <Box
              key={item.id}
              sx={{
                position: 'relative',
                height: 52,
              }}
            >
              <Box
                onClick={() => {
                  if (item.type === 'livechat') {
                    openLiveChat();
                  } else {
                    window.open(item.url, '_blank');
                  }
                }}
                sx={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  height: 52,
                  width: 52,
                  display: 'flex',
                  alignItems: 'center',
                  background: 'linear-gradient(180deg, #3f6b3f 0%, #1e3d2b 100%)',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  borderBottom: '1px solid rgba(255,255,255,0.08)',

                  '&:hover': {
                    width: 180,
                    backgroundColor: '#3a3a3a',
                  },

                  '&:hover .label': {
                    opacity: 1,
                    transform: 'translateX(0)',
                  },
                }}
              >
                {/* ICON */}
                <Box
                  sx={{
                    minWidth: 52,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <img src={item.icon} alt={item.name} width={24} height={24} />
                </Box>

                {/* TEXT */}
                <Typography
                  className="label"
                  sx={{
                    whiteSpace: 'nowrap',
                    opacity: 0,
                    transform: 'translateX(10px)',
                    transition: 'all 0.3s ease',
                  }}
                >
                  {translate(item.name)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {!isDesktop && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 240,
            right: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'stretch',
          }}
        >
          <Box
            onClick={() => setOpen(!open)}
            sx={{
              writingMode: 'vertical-rl',
              background: 'linear-gradient(180deg, #2e4d2c, #1f3520)',
              color: '#f9a825',
              px: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderTopLeftRadius: 12,
              borderBottomLeftRadius: 12,
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '12px',
              transition: 'all 0.35s ease',
              height: 'auto',
              py: 1.5,
            }}
          >
            {translate('contact_us')}
          </Box>

          {open && (
            <Box
              sx={{
                width: 180,
                background: 'linear-gradient(180deg, #2e4d2c, #1f3520)',
                color: '#fff',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {chatLinks.map((item) => (
                <Box
                  key={item.id}
                  onClick={() => {
                    if (item.type === 'livechat') {
                      openLiveChat();
                    } else {
                      window.open(item.url, '_blank');
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    cursor: 'pointer',
                  }}
                >
                  <img src={item.icon} alt={item.name} width={26} />
                  <Box>
                    <Typography fontSize={12} fontWeight={600}>
                      {translate(item.name)}
                    </Typography>
                    <Typography fontSize={12} color="gray">
                      {item.label}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default FloatingIcons;
